import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/auth";

type QuickAction = {
  resource: string;   // código del recurso (ruta) que debe tener el usuario
  path: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  bg: string;
};

// Catálogo de accesos rápidos; solo se muestran los que el usuario tiene en sus recursos
const quickActions: QuickAction[] = [
  { resource: "products", path: "/products", title: "Productos y menús", description: "Cree productos y organícelos por menú.", icon: "bi-bag", color: "#198754", bg: "#e8f6ee" },
  { resource: "stocks", path: "/stocks", title: "Inventario", description: "Controle el stock y los movimientos de sus productos.", icon: "bi-box-seam", color: "#fd7e14", bg: "#fff2e6" },
  { resource: "link-pago-admin", path: "/link-pago-admin", title: "Links de pago", description: "Cree y gestione links de pago para sus clientes.", icon: "bi-link-45deg", color: "#0d6efd", bg: "#e7f1ff" },
  { resource: "reports/transactions", path: "/reports/transactions", title: "Transacciones", description: "Consulte pagos, devoluciones y anulaciones.", icon: "bi-credit-card", color: "#6f42c1", bg: "#f0ebfa" },
  { resource: "electronic-invoice-settings", path: "/electronic-invoice-settings", title: "Facturación electrónica", description: "Configure su comercio ante Hacienda, sucursales y credenciales.", icon: "bi-receipt-cutoff", color: "#20c997", bg: "#e6f8f3" },
  { resource: "tables", path: "/tables", title: "Mesas", description: "Administre las mesas y el QR del menú.", icon: "bi-grid-3x3-gap", color: "#0dcaf0", bg: "#e6f9fd" },
  { resource: "suppliers", path: "/suppliers", title: "Proveedores", description: "Registre los proveedores de sus productos.", icon: "bi-truck", color: "#6c757d", bg: "#f1f3f5" },
  { resource: "users", path: "/users", title: "Usuarios", description: "Administre quién tiene acceso al sistema.", icon: "bi-people", color: "#d63384", bg: "#fbeaf2" },
  { resource: "merchants", path: "/merchants", title: "Comercios", description: "Administre los comercios afiliados.", icon: "bi-shop", color: "#0d6efd", bg: "#e7f1ff" },
];

const QuickAccess: React.FC = () => {
  const navigate = useNavigate();
  const { resources } = useContext(AuthContext);
  const [hovered, setHovered] = useState<string | null>(null);

  // Misma regla que el navbar: el recurso debe existir y ser de primer nivel o tener
  // a su padre disponible (el API puede quitar el padre según el tipo de comercio)
  const isReachable = (code: string) => {
    const resource = resources.find(r => r.code === code);
    if (!resource) return false;
    return resource.parentId === null || resources.some(r => r.id === resource.parentId);
  };

  const filteredActions = quickActions.filter(action => isReachable(action.resource));

  if (filteredActions.length === 0) return null;

  return (
    <div className="row g-3">
      {filteredActions.map((action) => (
        <div key={action.resource} className="col-12 col-md-6 col-xl-4">
          <div
            role="button"
            tabIndex={0}
            className="card border-0 shadow-sm h-100"
            style={{
              cursor: "pointer",
              transition: "transform .15s ease, box-shadow .15s ease",
              transform: hovered === action.resource ? "translateY(-2px)" : "none",
              boxShadow: hovered === action.resource ? "0 .5rem 1.25rem rgba(0,0,0,.1)" : undefined,
            }}
            onMouseEnter={() => setHovered(action.resource)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => navigate(action.path)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(action.path); }}
          >
            <div className="card-body d-flex align-items-center gap-3">
              <div style={{ ...styles.iconCircle, backgroundColor: action.bg, color: action.color }}>
                <i className={`bi ${action.icon}`} />
              </div>
              <div className="flex-grow-1">
                <div className="fw-semibold">{action.title}</div>
                <div className="text-muted small">{action.description}</div>
              </div>
              <i className="bi bi-chevron-right text-muted" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const styles = {
  iconCircle: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 22,
    flexShrink: 0,
  },
};

export default QuickAccess;
